import { motion } from 'framer-motion';
import { Shield, Download, Trash2, Eye, Edit, Ban, ArrowRight, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import StaticPageLayout from '../../components/StaticPageLayout';

const rights = [
  {
    icon: Eye,
    title: 'Right to Access',
    description:
      'You have the right to request a copy of all personal data we hold about you. We will provide this within 30 days of your request.',
    action: 'Request Data Export',
  },
  {
    icon: Edit,
    title: 'Right to Rectification',
    description:
      'If any of your personal data is inaccurate or incomplete, you have the right to request that we correct or complete it.',
    action: 'Update Information',
  },
  {
    icon: Trash2,
    title: 'Right to Erasure',
    description:
      'You can request that we delete your personal data. This is also known as the "right to be forgotten."',
    action: 'Delete My Data',
  },
  {
    icon: Ban,
    title: 'Right to Restrict Processing',
    description:
      'You can request that we limit how we use your personal data while we verify its accuracy or consider your objection.',
    action: 'Restrict Processing',
  },
  {
    icon: Download,
    title: 'Right to Data Portability',
    description:
      'You can request your data in a structured, machine-readable format to transfer to another service provider.',
    action: 'Export Data',
  },
  {
    icon: Shield,
    title: 'Right to Object',
    description:
      'You can object to the processing of your personal data for direct marketing or based on our legitimate interests.',
    action: 'Submit Objection',
  },
];

const dataCategories = [
  {
    category: 'Account Information',
    data: ['Name', 'Email address', 'Password (hashed)', 'Profile picture', 'Google OAuth tokens'],
    purpose: 'Account management and authentication',
    retention: 'Until account deletion + 90 days',
  },
  {
    category: 'Chat Data',
    data: ['Messages', 'AI responses', 'Voice-to-text transcripts', 'Conversation history', 'Timestamps'],
    purpose: 'Providing AI employee services',
    retention: 'Until account deletion + 30 days',
  },
  {
    category: 'Brain AI Data',
    data: ['Uploaded documents', 'Custom training data', 'Knowledge base content', 'URL-sourced content'],
    purpose: 'Personalizing AI employee responses',
    retention: 'Until explicitly deleted + 7 days',
  },
  {
    category: 'Social Media & Communication Data',
    data: ['Connected accounts', 'Scheduled posts', 'Email messages', 'WhatsApp messages'],
    purpose: 'Managing social media and communications on your behalf',
    retention: 'Until account deletion + 30 days',
  },
  {
    category: 'Usage Data',
    data: ['Features used', 'Credits consumed', 'Login times', 'Device information'],
    purpose: 'Service improvement and analytics',
    retention: '2 years',
  },
  {
    category: 'Billing Data',
    data: ['Transaction history', 'Invoices', 'Subscription status', 'Gift cards'],
    purpose: 'Payment processing and accounting',
    retention: '7 years (legal requirement)',
  },
];

export default function GDPR() {
  return (
    <StaticPageLayout
      title="GDPR Compliance"
      subtitle="How SweetPilot protects and respects your data rights under the General Data Protection Regulation."
    >
      {/* Overview */}
      <section className="mb-12">
        <div className="p-8 rounded-2xl bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/5">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-purple-500/10 flex items-center justify-center shrink-0">
              <Shield className="w-7 h-7 text-purple-400" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white mb-3">Our Commitment to GDPR</h2>
              <p className="text-gray-300">
                SweetPilot is committed to full compliance with the General Data Protection Regulation (GDPR). We respect your privacy rights and have implemented comprehensive measures to protect your personal data. This page explains how we comply with GDPR and how you can exercise your rights.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Your Rights */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold text-white mb-8">Your Data Rights</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rights.map((right, index) => (
            <motion.div
              key={right.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="p-6 rounded-2xl bg-dark-800/50 border border-white/5 flex flex-col"
            >
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4">
                <right.icon className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">{right.title}</h3>
              <p className="text-gray-400 text-sm flex-1 mb-4">{right.description}</p>
              <Link
                to="/dashboard/settings"
                className="flex items-center gap-2 text-purple-400 hover:text-purple-300 text-sm font-medium"
              >
                {right.action} <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Data We Collect */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold text-white mb-8">Data We Collect & Why</h2>
        <div className="space-y-4">
          {dataCategories.map((item, index) => (
            <motion.div
              key={item.category}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="p-6 rounded-2xl bg-dark-800/50 border border-white/5"
            >
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">{item.category}</h3>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {item.data.map((d) => (
                      <span
                        key={d}
                        className="px-2 py-1 rounded-lg bg-dark-700 text-gray-400 text-xs"
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-gray-400">
                    <strong className="text-gray-300">Purpose:</strong> {item.purpose}
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-sm text-gray-500">Retention</span>
                  <p className="text-white font-medium">{item.retention}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Legal Bases */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold text-white mb-8">Legal Bases for Processing</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            {
              title: 'Contract Performance',
              description:
                'Processing necessary to provide our services to you under our Terms of Service.',
              examples: 'Account creation, chat processing, subscription management',
            },
            {
              title: 'Legitimate Interests',
              description:
                'Processing for our legitimate business interests, balanced against your rights.',
              examples: 'Security measures, fraud prevention, service improvements',
            },
            {
              title: 'Consent',
              description: 'Processing based on your explicit consent, which you can withdraw anytime.',
              examples: 'Marketing emails, analytics cookies, optional features',
            },
            {
              title: 'Legal Obligation',
              description: 'Processing required to comply with legal requirements.',
              examples: 'Tax records, financial reporting, legal requests',
            },
          ].map((basis) => (
            <div
              key={basis.title}
              className="p-6 rounded-2xl bg-dark-800/50 border border-white/5"
            >
              <h3 className="text-lg font-semibold text-white mb-2">{basis.title}</h3>
              <p className="text-gray-400 text-sm mb-3">{basis.description}</p>
              <p className="text-sm">
                <span className="text-gray-500">Examples: </span>
                <span className="text-gray-300">{basis.examples}</span>
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Data Transfers */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold text-white mb-6">International Data Transfers</h2>
        <div className="p-6 rounded-2xl bg-dark-800/50 border border-white/5">
          <p className="text-gray-300 mb-4">
            SweetPilot operates globally, and your data may be transferred to and processed in countries outside the European Economic Area (EEA). We ensure adequate protection through:
          </p>
          <ul className="space-y-3">
            {[
              'Standard Contractual Clauses (SCCs) approved by the European Commission',
              'Data processing agreements with all third-party processors',
              'Adequacy decisions where applicable',
              'Additional technical and organizational measures',
            ].map((item) => (
              <li key={item} className="flex items-start gap-2 text-gray-300">
                <span className="text-green-400 mt-0.5">✓</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Data Protection Officer */}
      <section className="mb-16">
        <div className="p-8 rounded-2xl bg-dark-800/50 border border-white/5">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Data Protection Officer</h2>
              <p className="text-gray-400">
                Our DPO is available to answer any questions about our data practices and help you exercise your rights.
              </p>
            </div>
            <a
              href="mailto:dpo@sweetpilot.com"
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white shrink-0"
            >
              Contact DPO <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Submit Request */}
      <section className="text-center p-8 rounded-2xl bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/5">
        <FileText className="w-12 h-12 text-purple-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-white mb-4">Submit a Data Request</h2>
        <p className="text-gray-400 mb-6 max-w-md mx-auto">
          To exercise any of your GDPR rights, submit a request through your account settings or contact our DPO directly.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/dashboard/settings"
            className="px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white"
          >
            Go to Settings
          </Link>
          <a
            href="mailto:dpo@sweetpilot.com"
            className="px-6 py-3 bg-dark-700 text-white rounded-xl font-medium hover:bg-dark-600 transition-colors"
          >
            Email DPO
          </a>
        </div>
      </section>
    </StaticPageLayout>
  );
}
